import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tutorial.css';
import { buildTutorialSteps } from './tutorialSteps';
import { isTutorialCompleted, markTutorialCompleted } from './tutorialStorage';

const MOBILE_SIDEBAR_MQ = '(max-width: 1023px)';
const TutorialContext = createContext(null);

function isMobileViewport() {
  return window.matchMedia(MOBILE_SIDEBAR_MQ).matches;
}

function resolveAvailableSteps(steps) {
  return steps.filter((step) => {
    if (!step.element) return true;
    return Boolean(document.querySelector(step.element));
  });
}

export function TutorialProvider({
  children,
  setSidebarCollapsed,
  setForceDocumentsOpen,
  setForceCrmOpen,
}) {
  const location = useLocation();
  const driverRef = useRef(null);
  const autoStartedRef = useRef(false);

  const resetChrome = useCallback(() => {
    setForceDocumentsOpen?.(false);
    setForceCrmOpen?.(false);
    if (isMobileViewport()) {
      setSidebarCollapsed?.(true);
    }
  }, [setForceCrmOpen, setForceDocumentsOpen, setSidebarCollapsed]);

  const destroyDriverInstance = useCallback(() => {
    const instance = driverRef.current;
    driverRef.current = null;
    // Public destroy() skips onDestroyStarted and tears down the overlay.
    instance?.destroy();
  }, []);

  const completeAndDestroy = useCallback(
    (activeDriver) => {
      markTutorialCompleted();
      resetChrome();
      driverRef.current = null;
      // activeDriver.destroy() === internal destroy(false)
      activeDriver?.destroy();
    },
    [resetChrome]
  );

  const applyStepChrome = useCallback(
    (step) => {
      const meta = step?.meta || {};

      if (meta.requiresDocumentsOpen) {
        setForceDocumentsOpen?.(true);
      }

      if (meta.requiresCrmOpen) {
        setForceCrmOpen?.(true);
      }

      if (meta.requiresSidebar && isMobileViewport()) {
        setSidebarCollapsed?.(false);
      }

      if (meta.closeSidebarOnMobile && isMobileViewport()) {
        setSidebarCollapsed?.(true);
      }
    },
    [setForceCrmOpen, setForceDocumentsOpen, setSidebarCollapsed]
  );

  const startTutorial = useCallback(() => {
    destroyDriverInstance();

    // Expand both collapsible menus so submenu tour targets exist in the DOM.
    setForceDocumentsOpen?.(true);
    setForceCrmOpen?.(true);
    if (isMobileViewport()) {
      setSidebarCollapsed?.(false);
    }

    const launch = () => {
      const steps = resolveAvailableSteps(buildTutorialSteps());
      if (steps.length === 0) return;

      const driverObj = driver({
        showProgress: true,
        animate: true,
        allowClose: true,
        smoothScroll: true,
        disableActiveInteraction: true,
        overlayOpacity: 0.55,
        stagePadding: 8,
        stageRadius: 8,
        popoverClass: 'neo-tutorial-popover',
        nextBtnText: 'Próximo passo',
        prevBtnText: 'Anterior',
        doneBtnText: 'Concluir',
        progressText: 'Passo {{current}} de {{total}}',
        showButtons: ['next', 'previous', 'close'],
        steps,
        onPopoverRender: (popover) => {
          if (!popover.closeButton) return;
          popover.closeButton.textContent = 'Pular tutorial';
          popover.closeButton.setAttribute('aria-label', 'Pular tutorial');
          popover.closeButton.title = 'Pular tutorial';
          if (
            popover.footer &&
            popover.closeButton.parentElement !== popover.footer
          ) {
            popover.footer.insertBefore(
              popover.closeButton,
              popover.footer.firstChild
            );
          }
        },
        onHighlightStarted: (_element, step) => {
          applyStepChrome(step);
        },
        onDestroyStarted: (_element, _step, { driver: activeDriver }) => {
          // Close / done call destroy(true) → this hook. We must tear down.
          completeAndDestroy(activeDriver);
        },
      });

      driverRef.current = driverObj;
      driverObj.drive();
    };

    window.setTimeout(launch, 320);
  }, [
    applyStepChrome,
    completeAndDestroy,
    destroyDriverInstance,
    setForceCrmOpen,
    setForceDocumentsOpen,
    setSidebarCollapsed,
  ]);

  useEffect(() => {
    if (location.pathname !== '/contract-dashboard') {
      if (driverRef.current) {
        markTutorialCompleted();
        resetChrome();
        destroyDriverInstance();
      }
      return undefined;
    }

    if (isTutorialCompleted() || autoStartedRef.current) {
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;

    const tryAutoStart = () => {
      if (cancelled || autoStartedRef.current) return;

      const ready =
        document.querySelector('[data-tour="tour-create-from-model"]') ||
        document.querySelector('[data-tour="tour-home"]');
      if (
        document.querySelector('[data-tour="tour-create-from-model"]') ||
        attempts >= 24
      ) {
        autoStartedRef.current = true;
        if (ready && !isTutorialCompleted()) {
          startTutorial();
        }
        return;
      }

      attempts += 1;
      window.setTimeout(tryAutoStart, 250);
    };

    const initialTimer = window.setTimeout(tryAutoStart, 500);
    return () => {
      cancelled = true;
      window.clearTimeout(initialTimer);
    };
  }, [destroyDriverInstance, location.pathname, resetChrome, startTutorial]);

  useEffect(
    () => () => {
      destroyDriverInstance();
    },
    [destroyDriverInstance]
  );

  const value = useMemo(() => ({ startTutorial }), [startTutorial]);

  return (
    <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>
  );
}

export function useTutorial() {
  const ctx = useContext(TutorialContext);
  if (!ctx) {
    throw new Error('useTutorial must be used within TutorialProvider');
  }
  return ctx;
}

export function useTutorialOptional() {
  return useContext(TutorialContext);
}
